import type { ReactElement, ReactNode } from "react"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import Link from "@onestore/hel/dist/components/quarks/Link"
import { PeriodInfo } from "@core/period-info"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import DetailedStaticPrice from "@gatsby-plugin-generic-page/components/DetailedStaticPrice"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type {
  ProductCardsGridSectionCard,
  ProductCardsGridSectionFlatData,
} from "@gatsby-plugin-generic-page/fragments/productCardsGridSection"
import { getBgColorClass } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import { getStaticPrices } from "@gatsby-plugin-generic-page/helpers/staticPrices"
import { SquidexReferenceType } from "@gatsby-plugin-generic-page/types/referenceType"
import { Box, Button, Text } from "@ionos-web-design-system/react"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { StaticPrice } from "~/fragments/staticPrice"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import type { CloudBlueDomain } from "~/types/squidex"

type ProductCardProps = ProductCardsGridSectionCard & {
  isTabletFullVariant?: boolean
}

export default function ProductCard(
  card: ProductCardProps
): ReactElement<ProductCardsGridSectionFlatData> {
  const { currentPriceType } = usePriceTypeContext()

  const visualProps = !isEmpty(card.icon)
    ? {
        icon: true,
        iconContent: <Media media={card.icon} />,
      }
    : undefined

  let priceElement: ReactNode | null = null

  if (
    typeMatches(
      card.price[0].__typename,
      SquidexReferenceType.CLOUDBLUE_PLAN
    ) ||
    typeMatches(card.price[0].__typename, SquidexReferenceType.CLOUDBLUE_DOMAIN)
  ) {
    const { flatData } = card.price[0] as CloudBluePlan | CloudBlueDomain
    const defaultPeriod = flatData.periods.find((period) => period.default)

    if (defaultPeriod) {
      priceElement = (
        <DetailedPrice
          variant={{ type: PriceVariantType.MEDIUM, align: "left" }}
          layoutVariant="default"
          periodInfo={new PeriodInfo(defaultPeriod)}
          priceInfo={flatData.priceInfo}
        />
      )
    }
  } else if (
    typeMatches(card.price[0].__typename, SquidexReferenceType.STATIC_PRICE)
  ) {
    const staticPricesData = card.price as StaticPrice[]
    const staticPrices = getStaticPrices(staticPricesData, currentPriceType)
    const period = Object.keys(staticPrices)[0]

    priceElement = (
      <DetailedStaticPrice
        variant={{ type: PriceVariantType.MEDIUM, align: "left" }}
        layoutVariant="default"
        staticPrices={staticPricesData}
        period={period}
      />
    )
  }

  const { textWithEmbeddedContent, ...linkProps } = getLinkProps(
    card.link,
    currentPriceType
  )

  const bgColorClass = getBgColorClass(card.backgroundColor)

  const footerWrapperClassNames = "flex flex-col gap-4"

  const footerContent = (
    <div
      className={
        card.isTabletFullVariant
          ? cn(
              footerWrapperClassNames,
              "md:max-lg:gap-2 md:max-lg:flex-row md:max-lg:items-end md:max-lg:justify-between"
            )
          : footerWrapperClassNames
      }
    >
      {!isEmpty(priceElement) ? priceElement : null}

      <div>
        <Button
          concept="monochrome"
          variant="primary"
          size="medium"
          className="inline-block"
        >
          {textWithEmbeddedContent?.text}
        </Button>
      </div>
    </div>
  )

  return (
    <Link {...linkProps} className="contents">
      <Box
        key={card.title}
        visual={visualProps}
        footer={footerContent}
        className={cn(bgColorClass ?? "bg-surface-base", "shadow-sm h-full")}
      >
        <Text asChild variant="heading2xl" weight="bold" className="mb-3">
          <h3>{card.title}</h3>
        </Text>

        {!isEmpty(card.features) ? (
          <MarkdownText>{card.features}</MarkdownText>
        ) : null}
      </Box>
    </Link>
  )
}
